import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  UserPlus,
  Plus,
  Trash2,
  X,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Filter,
  ChevronDown,
  AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inactive statuses that should be marked differently
const INACTIVE_STATUSES = [
  'Hired Elsewhere',
  'Not a Fit',
  'Withdrew Application',
  'No Show',
  'Archived',
];

export default function Applicants() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applicants, setApplicants] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [positionFilter, setPositionFilter] = useState(searchParams.get('position') || '');
  const [locationFilter, setLocationFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [newApplicant, setNewApplicant] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    job_posting_id: '',
    desired_position: '',
    desired_coverage_areas: '',
    employment_type_preference: 'Full-time',
    available_start_date: '',
    source: '',
  });
  const [resumeFile, setResumeFile] = useState(null);

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    fetchApplicants();
  }, [statusFilter, positionFilter, locationFilter, includeInactive]);

  const fetchMetadata = async () => {
    try {
      const [statusRes, posTypes, empTypes, postingsRes] = await Promise.all([
        axios.get(`${API}/ats/statuses`),
        axios.get(`${API}/ats/position-types`),
        axios.get(`${API}/ats/employment-types`),
        axios.get(`${API}/ats/job-postings`),
      ]);
      setStatuses(statusRes.data.statuses);
      setPositionTypes(posTypes.data.position_types);
      setEmploymentTypes(empTypes.data.employment_types);
      setJobPostings(postingsRes.data);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchApplicants = async () => {
    try {
      const params = { include_inactive: includeInactive };
      if (statusFilter) params.status = statusFilter;
      if (positionFilter) params.desired_position = positionFilter;
      if (locationFilter) params.location = locationFilter;
      if (search) params.search = search;
      const response = await axios.get(`${API}/ats/applicants`, { params });
      setApplicants(response.data);
    } catch (error) {
      toast.error('Failed to load applicants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchApplicants();
  };

  const handleAddApplicant = async (e) => {
    e.preventDefault();
    if (!newApplicant.first_name || !newApplicant.last_name) {
      toast.error('First name and last name are required');
      return;
    }

    // Check for duplicates (skip if already confirmed)
    if (!duplicateWarning) {
      try {
        const res = await axios.post(`${API}/ats/applicants/check-duplicate`, {
          first_name: newApplicant.first_name,
          last_name: newApplicant.last_name,
          email: newApplicant.email,
        });
        if (res.data.duplicates && res.data.duplicates.length > 0) {
          setDuplicateWarning(res.data.duplicates);
          return;
        }
      } catch (err) {
        // If check fails, proceed
      }
    }

    try {
      const payload = {
        ...newApplicant,
        desired_coverage_areas: newApplicant.desired_coverage_areas
          ? newApplicant.desired_coverage_areas.split(',').map((a) => a.trim())
          : [],
      };
      const response = await axios.post(`${API}/ats/applicants`, payload);

      // Upload resume if one was selected
      if (resumeFile) {
        try {
          const fd = new FormData();
          fd.append('file', resumeFile);
          await axios.post(
            `${API}/ats/applicants/${response.data.id}/documents?document_name=${encodeURIComponent('Resume')}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (uploadErr) {
          toast.warning('Applicant added, but the resume failed to upload. You can add it on the applicant page.');
        }
      }

      toast.success('Applicant added successfully');
      setShowAddModal(false);
      setDuplicateWarning(null);
      setResumeFile(null);
      setNewApplicant({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        job_posting_id: '',
        desired_position: '',
        desired_coverage_areas: '',
        employment_type_preference: 'Full-time',
        available_start_date: '',
        source: '',
      });
      navigate(`/ats/applicants/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to add applicant');
    }
  };

  const handleDeleteApplicant = async (e, applicantId, name) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      await axios.delete(`${API}/ats/applicants/${applicantId}`);
      toast.success('Applicant deleted successfully');
      fetchApplicants();
    } catch (error) {
      toast.error('Failed to delete applicant');
    }
  };

  const getStatusColor = (status) => {
    if (INACTIVE_STATUSES.includes(status)) {
      return 'bg-gray-100 text-gray-600';
    }
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-800';
      case 'Waiting to Schedule 1st Interview':
      case 'Waiting to Schedule 2nd Interview':
        return 'bg-yellow-100 text-yellow-800';
      case '1st Interview Scheduled':
      case '2nd Interview Scheduled':
        return 'bg-purple-100 text-purple-800';
      case '1st Interview Complete':
      case '2nd Interview Complete':
        return 'bg-indigo-100 text-indigo-800';
      case 'Background Check':
        return 'bg-orange-100 text-orange-800';
      case 'Offer Extended':
        return 'bg-pink-100 text-pink-800';
      case 'Offer Accepted':
      case 'Hired':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Applicants
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Track and manage job applicants
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="gap-2"
          data-testid="add-applicant-btn"
        >
          <Plus size={18} />
          Add Applicant
        </Button>
      </div>

      <Card className="p-6">
        {/* Search and Filters */}
        <div className="space-y-4 mb-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                type="text"
                placeholder="Search by name, email, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="toggle-filters"
            >
              <Filter size={16} />
              Filters
              <ChevronDown
                size={16}
                className={`transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Status
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Position
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={positionFilter}
                  onChange={(e) => setPositionFilter(e.target.value)}
                  data-testid="position-filter"
                >
                  <option value="">All Positions</option>
                  {positionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Location
                </label>
                <Input
                  type="text"
                  placeholder="City or state..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="h-9"
                  data-testid="location-filter"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    className="h-4 w-4"
                    data-testid="include-inactive"
                  />
                  <span className="text-sm">Include Inactive</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading applicants...
          </div>
        ) : applicants.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus
              className="mx-auto mb-4 text-muted-foreground"
              size={48}
              strokeWidth={1.5}
            />
            <p className="text-muted-foreground">No applicants found</p>
            <Button
              className="mt-4"
              onClick={() => setShowAddModal(true)}
              data-testid="empty-add-applicant"
            >
              <Plus size={16} className="mr-2" />
              Add First Applicant
            </Button>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Contact
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Position
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Location
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Applied
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {applicants.map((applicant) => (
                  <tr
                    key={applicant.id}
                    className={`border-b border-border hover:bg-muted/20 transition-colors cursor-pointer ${
                      INACTIVE_STATUSES.includes(applicant.status) ? 'opacity-60' : ''
                    }`}
                    onClick={() => navigate(`/ats/applicants/${applicant.id}`)}
                    data-testid={`applicant-row-${applicant.id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium">
                        {applicant.first_name} {applicant.last_name}
                      </div>
                      {applicant.source && (
                        <div className="text-xs text-muted-foreground">
                          via {applicant.source}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-sm space-y-1">
                        {applicant.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail size={12} />
                            {applicant.email}
                          </div>
                        )}
                        {applicant.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone size={12} />
                            {applicant.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Briefcase size={14} className="text-muted-foreground" />
                        <span>{applicant.desired_position || '-'}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {applicant.employment_type_preference}
                      </div>
                    </td>
                    <td className="p-4">
                      {applicant.city || applicant.state ? (
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-muted-foreground" />
                          <span>
                            {[applicant.city, applicant.state]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={getStatusColor(applicant.status)}>
                        {applicant.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {formatDate(applicant.created_at)}
                    </td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) =>
                          handleDeleteApplicant(
                            e,
                            applicant.id,
                            `${applicant.first_name} ${applicant.last_name}`
                          )
                        }
                        data-testid={`delete-applicant-${applicant.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Applicant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add New Applicant</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddModal(false)}
              >
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleAddApplicant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    First Name *
                  </label>
                  <Input
                    value={newApplicant.first_name}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, first_name: e.target.value })
                    }
                    placeholder="First name"
                    data-testid="new-first-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Name *
                  </label>
                  <Input
                    value={newApplicant.last_name}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, last_name: e.target.value })
                    }
                    placeholder="Last name"
                    data-testid="new-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newApplicant.email}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, email: e.target.value })
                    }
                    placeholder="email@example.com"
                    data-testid="new-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Phone
                  </label>
                  <Input
                    value={newApplicant.phone}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, phone: e.target.value })
                    }
                    placeholder="(555) 123-4567"
                    data-testid="new-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Address
                </label>
                <Input
                  value={newApplicant.address}
                  onChange={(e) =>
                    setNewApplicant({ ...newApplicant, address: e.target.value })
                  }
                  placeholder="Street address"
                  data-testid="new-address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    City
                  </label>
                  <Input
                    value={newApplicant.city}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, city: e.target.value })
                    }
                    placeholder="City"
                    data-testid="new-city"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    State
                  </label>
                  <Input
                    value={newApplicant.state}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, state: e.target.value })
                    }
                    placeholder="TX"
                    data-testid="new-state"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Zip Code
                  </label>
                  <Input
                    value={newApplicant.zip_code}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, zip_code: e.target.value })
                    }
                    placeholder="77001"
                    data-testid="new-zip"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-3">Position Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Job Posting
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newApplicant.job_posting_id}
                      onChange={(e) =>
                        setNewApplicant({
                          ...newApplicant,
                          job_posting_id: e.target.value,
                        })
                      }
                      data-testid="new-job-posting"
                    >
                      <option value="">No specific posting</option>
                      {jobPostings.map((posting) => (
                        <option key={posting.id} value={posting.id}>
                          {posting.title} ({posting.position_type})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Desired Position
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newApplicant.desired_position}
                      onChange={(e) =>
                        setNewApplicant({
                          ...newApplicant,
                          desired_position: e.target.value,
                        })
                      }
                      data-testid="new-desired-position"
                    >
                      <option value="">Select...</option>
                      {positionTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Employment Preference
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newApplicant.employment_type_preference}
                      onChange={(e) =>
                        setNewApplicant({
                          ...newApplicant,
                          employment_type_preference: e.target.value,
                        })
                      }
                      data-testid="new-employment-type"
                    >
                      {employmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Available Start Date
                    </label>
                    <Input
                      type="date"
                      value={newApplicant.available_start_date}
                      onChange={(e) =>
                        setNewApplicant({
                          ...newApplicant,
                          available_start_date: e.target.value,
                        })
                      }
                      data-testid="new-start-date"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-sm font-medium text-muted-foreground">
                    Desired Coverage Areas (comma-separated)
                  </label>
                  <Input
                    value={newApplicant.desired_coverage_areas}
                    onChange={(e) =>
                      setNewApplicant({
                        ...newApplicant,
                        desired_coverage_areas: e.target.value,
                      })
                    }
                    placeholder="e.g., Houston, Katy, Sugar Land"
                    data-testid="new-coverage-areas"
                  />
                </div>

                <div className="mt-4">
                  <label className="text-sm font-medium text-muted-foreground">
                    Source
                  </label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newApplicant.source}
                    onChange={(e) =>
                      setNewApplicant({ ...newApplicant, source: e.target.value })
                    }
                    data-testid="new-source"
                  >
                    <option value="">Select source...</option>
                    <option value="Indeed">Indeed</option>
                    <option value="Glassdoor">Glassdoor</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Referral">Referral</option>
                    <option value="Website">Website</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label className="text-sm font-medium text-muted-foreground">
                    Resume (optional)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm"
                    data-testid="new-resume-file"
                  />
                  {resumeFile && (
                    <p className="text-xs text-muted-foreground mt-1" data-testid="new-resume-filename">
                      Selected: {resumeFile.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddModal(false); setDuplicateWarning(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-new-applicant">
                  {duplicateWarning ? 'Add Anyway' : 'Add Applicant'}
                </Button>
              </div>

              {/* Duplicate Warning */}
              {duplicateWarning && duplicateWarning.length > 0 && (
                <div className="p-4 rounded-lg border border-amber-300 bg-amber-50" data-testid="applicant-duplicate-warning">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={18} className="text-amber-600" />
                    <span className="font-semibold text-amber-800">Possible duplicate found</span>
                  </div>
                  <div className="space-y-2 mb-3">
                    {duplicateWarning.map((dup, i) => (
                      <div key={i} className="text-sm text-amber-700 flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{dup.first_name} {dup.last_name}</span>
                        {dup.email && <span className="text-amber-600">({dup.email})</span>}
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">{dup.source === 'personnel' ? 'Personnel' : 'Applicant'}</Badge>
                        <Badge className="text-[10px] bg-gray-100 text-gray-600">{dup.status || 'Unknown'}</Badge>
                        {dup.desired_position && <span className="text-xs text-amber-600">{dup.desired_position}</span>}
                        {dup.hire_date && <span className="text-xs text-amber-600">Hired {dup.hire_date}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600">Click "Add Anyway" to create a new record.</p>
                </div>
              )}
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
